import { QueryFunctionContext, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "../../../use-access-token";
import { queryKeys } from "../../queryKeys";
import { configuration } from "../../../../configuration";
import { FetchError, GetAgendaItemResourcesDto } from "../../../types";
import { useSignalR } from "../../../../hooks";
import { useEffect } from "react";
import { SignalRGroup } from "../../../../components";

const useGetAgendaItemResourcesRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getAgendaItemResourcesRequest({
    queryKey,
  }: QueryFunctionContext<
    ReturnType<typeof queryKeys.meetings.getAgendaItemResources>
  >): Promise<GetAgendaItemResourcesDto> {
    const accessToken = await getAccessToken();
    const [{ agendaItemGuid }] = queryKey;

    const response = await fetch(`${configuration.apiRootUrl}/agenda-items/${agendaItemGuid}/resources`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getAgendaItemResourcesRequest };
};

const useGetAgendaItemResources = (agendaItemGuid: string) => {
  const { getAgendaItemResourcesRequest } = useGetAgendaItemResourcesRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.MeetingAgendaItemContent(agendaItemGuid));
    return () => signalR.leaveGroup(SignalRGroup.MeetingAgendaItemContent(agendaItemGuid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendaItemGuid]);

  useEffect(() => {
    const handleShouldRefetchAgendaItemContent = (agendaItemGuid: string) => {
      queryClient.invalidateQueries(queryKeys.meetings.getAgendaItemResources(agendaItemGuid));
    };

    signalR.onShouldRefetchAgendaItemContent(handleShouldRefetchAgendaItemContent);
    return () => signalR.onShouldRefetchAgendaItemContent(handleShouldRefetchAgendaItemContent);
  }, [queryClient, signalR]);

  return useQuery({
    queryKey: queryKeys.meetings.getAgendaItemResources(agendaItemGuid),
    queryFn: getAgendaItemResourcesRequest,
  });
};

export { useGetAgendaItemResources };
